/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { getOrderHistoryList, getStoreConfig } from '@/account/api';
import { OrderDetails } from '@/account/data/models';
import { UseOrdersListProps } from '@/account/types';
import { useEffect, useState } from 'preact/hooks';
import { events } from '@adobe-commerce/event-bus';

export const useOrdersList = ({
  ordersInMinifiedView,
  minifiedView,
  pageSize,
  selectedDate,
  selectedPage,
  handleSetFirstOrderDate,
}: UseOrdersListProps) => {
  const [orderHistoryListItems, setOrderHistoryListItems] = useState<
    OrderDetails[]
  >([]);
  const [pageInfo, setPageInfo] = useState<{
    totalPages: number;
    currentPage: number;
    pageSize: number;
  }>({ totalPages: 1, currentPage: 1, pageSize: 1 });
  const [loading, setLoading] = useState(false);
  const [placeholderImage, setPlaceholderImage] = useState('');
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    getStoreConfig().then((response) => {
      setPlaceholderImage(response.baseMediaUrl);
    });
  }, []);

  // Listen for company context changes to refetch orders
  useEffect(() => {
    const companyContextEvent = events.on('companyContext/changed', () => {
      setRefetchTrigger(prev => prev + 1);
    });

    return () => {
      companyContextEvent?.off();
    };
  }, []);

  useEffect(() => {
    setLoading(true);

    getOrderHistoryList(
      minifiedView ? ordersInMinifiedView : pageSize,
      selectedDate,
      selectedPage
    )
      .then((response) => {
        if (!response || !response.items) return;

        setPageInfo(response.pageInfo);
        setOrderHistoryListItems(response.items);
        handleSetFirstOrderDate?.(response.dateOfFirstOrder);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    handleSetFirstOrderDate,
    minifiedView,
    ordersInMinifiedView,
    pageSize,
    selectedDate,
    selectedPage,
    refetchTrigger,
  ]);

  return {
    loading,
    orderHistoryListItems,
    pageInfo,
    placeholderImage,
  };
};
